import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import esLocale from "@fullcalendar/core/locales/es";
import { useDispatch } from "react-redux";
import { handleGetRequest } from "../../../service/GetTemplate";
import { handlePostRequest } from "../../../service/PostTemplate";
import { handleDeleteRequest } from "../../../service/DeleteTemplate";
import { endPoints } from "../../../Config";
import { exportToExcel } from "../../../utilities/exportToExcel";
import {
  DEFAULT_ROUTE_OPTIONS,
  ROUTE_OPTIONS,
} from "../../../utilities/constants";
import PhotoUploader from "../../../components/PhotoUploader";
import { getPreferredLanguage } from "../../../utilities/languagePreference";
import {
  translateUiText,
  translateUiToken,
} from "../../../utilities/siteTranslations";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./PreventiveMaintenanceReport.css";
import { filterClosedTasks, getTaskTimeliness } from "./closedTaskFilters";

const DEFAULT_CATEGORY_OPTIONS = [{ label: "All Categories", value: null }];
const DEFAULT_PREVENTIVE_TYPE = "operator";
const MAX_COMPLETION_PHOTOS = 10;

const PREVENTIVE_TYPE_OPTIONS = [
  { label: "Operator Preventive", value: "operator" },
  { label: "Technical Preventive", value: "technical" },
];

const RECURRENCE_OPTIONS = [
  { label: "Does not repeat", value: "none" },
  { label: "Every week", value: "every_week" },
  { label: "Every month", value: "every_month" },
  { label: "Every 3 months", value: "every_quarter" },
];

// Task type acts as the category for adherence analytics
const TASK_TYPE_OPTIONS = [
  { label: "General Cleaning", value: "General Cleaning" },
  { label: "Filter Replacement", value: "Filter Replacement" },
  { label: "Fumigation", value: "Fumigation" },
  { label: "Fumigation and General Cleaning", value: "Fumigation and General Cleaning" },
  { label: "Other", value: "Other" },
];

const TECHNICAL_TASK_CATEGORIES = TASK_TYPE_OPTIONS;

const OPERATOR_TASK_CATEGORIES = [
  {
    label: "Machine Stocking",
    value: "Machine Stocking",
    items: [
      "Check expiration dates",
      "Remove damaged product",
      "Arrange tight spiral products so they drop correctly",
    ],
  },
  {
    label: "Cleaning and Order",
    value: "Cleaning and Order",
    items: [
      "Clean machine exterior",
      "Basic internal cleaning without technical disassembly",
      "Clean snack trays",
      "Clean coffee containers",
      "Clean area around the machine",
      "Remove waste",
    ],
  },
  {
    label: "Hygiene and Pest Control",
    value: "Hygiene and Pest Control",
    items: [
      "Visual inspection for pest presence",
      "Clean critical areas",
      "Apply fumigation according to program",
      "Clean compressor grille",
    ],
  },
  {
    label: "Payment System Verification",
    value: "Payment System Verification",
    items: [
      "Test POS card payment",
      "Verify POS TDC version",
      "Validate coin acceptance",
      "Validate bill acceptance",
      "Confirm there is no stuck credit",
    ],
  },
  {
    label: "Machine Operational Verification",
    value: "Machine Operational Verification",
    items: [
      "Run spiral turn test",
      "Validate correct product drop",
      "Review spiral alignment",
      "Verify there are no stuck products",
      "Verify beverage dispensing",
      "Validate cup delivery",
      "Check for leaks",
    ],
  },
  {
    label: "Allowed Basic Adjustments",
    value: "Allowed Basic Adjustments",
    items: [
      "Arrange misplaced products",
      "Relocate spirals",
      "Adjust trays",
      "Basic machine restart if applicable",
      "Change coin mechanism",
      "Change telemeter",
      "Change lamps",
      "Change cup system",
      "Change sugar system",
      "Change containers",
      "Change spirals",
    ],
  },
  {
    label: "Identification and Labeling",
    value: "Identification and Labeling",
    items: [
      "Place QR on machine",
      "Replace damaged or missing QR",
      "Change instruction labels",
      "Place basic labeling",
      "Replace deteriorated labels",
    ],
  },
];

const TASK_CATEGORY_ITEMS_BY_VALUE = [
  ...TECHNICAL_TASK_CATEGORIES.map((category) => ({
    ...category,
    items: [category.label],
  })),
  ...OPERATOR_TASK_CATEGORIES,
].reduce((acc, category) => {
  acc[category.value] = category.items;
  return acc;
}, {});

const CALENDAR_STATUS_COLORS = {
  completed: "#22c55e",
  overdue: "#ef4444",
};

const TASK_TYPE_COLORS = {
  "General Cleaning": "#f97316",
  "Filter Replacement": "#3b82f6",
  Fumigation: "#a855f7",
  "Fumigation and General Cleaning": "#14b8a6",
  Other: "#6b7280",
};

const getTaskTypeColor = (category) =>
  TASK_TYPE_COLORS[category] || TASK_TYPE_COLORS.Other;

const resolveCalendarEventColor = (task, todayKey) => {
  if (task.status === "Completed") return CALENDAR_STATUS_COLORS.completed;
  const isOverdue =
    task.status === "Missed" ||
    (task.scheduledDateKey && todayKey && task.scheduledDateKey < todayKey);
  if (isOverdue) return CALENDAR_STATUS_COLORS.overdue;
  return getTaskTypeColor(task.category);
};

const padDatePart = (value) => String(value).padStart(2, "0");

const formatLocalDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${padDatePart(
    date.getMonth() + 1
  )}-${padDatePart(date.getDate())}`;
};

const formatUtcDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getUTCFullYear()}-${padDatePart(
    date.getUTCMonth() + 1
  )}-${padDatePart(date.getUTCDate())}`;
};

const getClosedTaskDefaultRange = () => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setMonth(start.getMonth() - 1);
  start.setHours(0, 0, 0, 0);

  return { start, end };
};

const getCalendarMonthRange = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  start.setHours(0, 0, 0, 0);

  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const EMPTY_FILTERS = {
  startDate: null,
  endDate: null,
  category: null,
  preventiveType: null,
  teamId: null,
  machineIds: [],
  route: null,
  executive: null,
};

const normalizeMachineLabel = (machine, t = (value) => value) =>
  [
    machine?.machine || machine?.name || t("Unnamed Machine"),
    machine?.type
      ? t(
          String(machine.type).charAt(0).toUpperCase() +
            String(machine.type).slice(1).toLowerCase()
        )
      : t("Unknown Type"),
    machine?.location || t("No location"),
    machine?.serialNumber ||
      machine?.deviceId ||
      machine?.uuid ||
      t("No ID"),
  ].join(" - ");

const PENDING_STATUSES = new Set(["Scheduled", "In Progress"]);
const CLOSED_STATUSES = new Set(["Completed", "Missed"]);

const filterMaintenanceData = (data, filters) => {
  let filtered = [...data];
  const startDateKey = filters.startDate
    ? formatLocalDateKey(filters.startDate)
    : "";
  const endDateKey = filters.endDate ? formatLocalDateKey(filters.endDate) : "";

  if (startDateKey) {
    filtered = filtered.filter(
      (task) => task.scheduledDateKey && task.scheduledDateKey >= startDateKey
    );
  }

  if (endDateKey) {
    filtered = filtered.filter(
      (task) => task.scheduledDateKey && task.scheduledDateKey <= endDateKey
    );
  }

  if (filters.category) {
    filtered = filtered.filter((task) => task.category === filters.category);
  }

  if (filters.preventiveType) {
    filtered = filtered.filter(
      (task) => task.preventiveType === filters.preventiveType
    );
  }

  if (filters.teamId) {
    filtered = filtered.filter(
      (task) => task.assignedToTeamId === filters.teamId
    );
  }

  if (filters.route) {
    filtered = filtered.filter((task) => task.route === filters.route);
  }

  if (Array.isArray(filters.machineIds) && filters.machineIds.length > 0) {
    const selectedSet = new Set(filters.machineIds.map(String));
    filtered = filtered.filter((task) =>
      Array.isArray(task.machineIds) &&
      task.machineIds.some((id) => selectedSet.has(String(id)))
    );
  }

  if (filters.executive) {
    filtered = filtered.filter((task) =>
      task.assignedToId && task.assignedToId === filters.executive
    );
  }

  return filtered;
};

const PreventiveMaintenanceReport = () => {
  const [preferredLanguage, setPreferredLanguage] = useState(
    getPreferredLanguage()
  );
  const [pmData, setPmData] = useState([]);
  const [closedFilters, setClosedFilters] = useState({});
  const [closedRange, setClosedRange] = useState(getClosedTaskDefaultRange);
  const [closedDateDraft, setClosedDateDraft] = useState(closedRange);
  const [closedFirst, setClosedFirst] = useState(0);
  const [closedRows, setClosedRows] = useState(10);
  const [filteredData, setFilteredData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshingData, setRefreshingData] = useState(false);
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [calendarRange, setCalendarRange] = useState(() =>
    getCalendarMonthRange(new Date())
  );
  const [summary, setSummary] = useState({
    total: 0,
    completed: 0,
    missed: 0,
    adherenceRate: 0,
  });
  const [categorySummary, setCategorySummary] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState(
    DEFAULT_CATEGORY_OPTIONS
  );
  const [routeOptions, setRouteOptions] = useState(DEFAULT_ROUTE_OPTIONS);
  const [machines, setMachines] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [teams, setTeams] = useState([]);
  const [maintenanceTeamIds, setMaintenanceTeamIds] = useState(new Set());
  const [spareParts, setSpareParts] = useState([]);
  const [scheduleDialogVisible, setScheduleDialogVisible] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    taskCategories: [],
    preventiveType: DEFAULT_PREVENTIVE_TYPE,
    assignedTo: "",
    route: "",
    machineIds: [],
    scheduledDate: null,
    recurrenceRule: "none",
    comments: "",
    partsUsed: [],
    supervisorSignature: "",
  });
  const [completionDialogVisible, setCompletionDialogVisible] = useState(false);
  const [savingCompletion, setSavingCompletion] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [calendarDetailVisible, setCalendarDetailVisible] = useState(false);
  const [calendarDetailTask, setCalendarDetailTask] = useState(null);
  const [referenceDataLoaded, setReferenceDataLoaded] = useState(false);
  const [loadingReferenceData, setLoadingReferenceData] = useState(false);
  const [completionForm, setCompletionForm] = useState({
    taskId: null,
    status: "Completed",
    completionNotes: "",
    taskCategories: [],
    completionChecklistItems: [],
    completionPhotos: [],
    partsUsed: [],
    supervisorSignature: "",
  });
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const hasLoadedInitialData = useRef(false);
  const dispatch = useDispatch();
  const currentUser = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const currentAdmin = currentUser?.data?.adminUser || {};
  const currentRoleUid = currentAdmin?.role?.uid;
  const isTechnician = currentRoleUid === "technician";
  const isOperator = currentRoleUid === "operator";
  const isScopedRole = isTechnician || isOperator;
  const t = (value) => translateUiText(value, preferredLanguage);
  const recurrenceOptions = RECURRENCE_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));
  const taskTypeOptions = TASK_TYPE_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));
  const operatorTaskCategoryOptions = OPERATOR_TASK_CATEGORIES.map((option) => ({
    ...option,
    label: t(option.label),
  }));
  const getPreventiveTypeLabel = (value) => {
    const option = PREVENTIVE_TYPE_OPTIONS.find((item) => item.value === value);
    return t(option?.label || "Operator Preventive");
  };

  const getSelectedAssignee = (assignedTo = scheduleForm.assignedTo) =>
    executives.find((executive) => executive._id === assignedTo) || null;

  const getAssigneeRoleUid = (assignedTo = scheduleForm.assignedTo) =>
    getSelectedAssignee(assignedTo)?.role?.uid || "";

  const getTaskCategoryOptionsForAssignee = (assignedTo = scheduleForm.assignedTo) => {
    const roleUid = getAssigneeRoleUid(assignedTo);
    if (roleUid === "operator") return operatorTaskCategoryOptions;
    return taskTypeOptions;
  };

  const getPreventiveTypeForAssignee = (assignedTo = scheduleForm.assignedTo) =>
    getAssigneeRoleUid(assignedTo) === "technician" ? "technical" : "operator";

  const getTaskCategoryLabel = (value) => {
    const allOptions = [...TASK_TYPE_OPTIONS, ...OPERATOR_TASK_CATEGORIES];
    const option = allOptions.find((item) => item.value === value);
    return t(option?.label || value || "Other");
  };

  const getCompletionChecklistOptions = (categories = []) =>
    categories.flatMap((category) =>
      (TASK_CATEGORY_ITEMS_BY_VALUE[category] || [category]).map((item) => ({
        label: t(item),
        value: item,
      }))
    );

  const appendTechnicianScope = (params) => {
    if (isScopedRole && currentAdmin?._id) {
      params.append("assignedTo", currentAdmin._id);
    }
  };

  const buildTaskRow = (task, index) => {
    const machinesArr = Array.isArray(task.machines) ? task.machines : [];
    const firstMachine = machinesArr[0] || {};
    const customer = firstMachine.location || "-";
    const machineId = firstMachine.serialNumber || firstMachine.deviceId || "-";
    const machineType = firstMachine.model || "";
    const machineCount = machinesArr.length;
    const machineIds = machinesArr
      .map((m) => m?._id)
      .filter((id) => !!id);
    const status = task.status || "Scheduled";
    const scheduledDateKey = task.scheduledDate
      ? formatUtcDateKey(task.scheduledDate)
      : "";

    return {
      id: task._id || index,
      taskId: task._id || "",
      taskCategories: Array.isArray(task.taskCategories) && task.taskCategories.length > 0
        ? task.taskCategories
        : [task.taskType].filter(Boolean),
      category:
        Array.isArray(task.taskCategories) && task.taskCategories.length > 0
          ? task.taskCategories.map(getTaskCategoryLabel).join(", ")
          : task.taskType,
      preventiveType: task.preventiveType || DEFAULT_PREVENTIVE_TYPE,
      customer,
      customers: [...new Set(machinesArr.map((machine) => machine.location).filter(Boolean))],
      route: task.route || "",
      executive: task.assignedTo
        ? `${task.assignedTo.firstName || ""} ${
            task.assignedTo.lastName || ""
          }`.trim()
        : "",
      assignedToId:
        task.assignedTo && typeof task.assignedTo === "object"
          ? task.assignedTo._id || ""
          : "",
      assignedToTeamId:
        task.assignedTo && typeof task.assignedTo === "object"
          ? String(task.assignedTo.teamId || task.assignedTo.team?._id || "")
          : "",
      machineId,
      machineType,
      machineCount,
      machineIds,
      scheduledDate: scheduledDateKey,
      scheduledDateKey,
      completedDate: task.completedAt || null,
      status,
      partsUsed: task.partsUsed || [],
      rawPartsUsed: task.partsUsed,
      completionNotes: task.completionNotes || "",
      completionChecklistItems: Array.isArray(task.completionChecklistItems)
        ? task.completionChecklistItems
        : [],
      completionPhoto: task.completionPhoto || "",
      completionPhotos: Array.isArray(task.completionPhotos)
        ? task.completionPhotos
        : [],
      supervisorSignature: task.supervisorSignature || "",
      scheduledComments: task.comments || "",
    };
  };

  const normalizeTaskListResponse = (response) => {
    const rawTasks = Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data)
      ? response.data
      : [];

    return rawTasks.map((task, index) => buildTaskRow(task, index));
  };

  const normalizeTaskDetailResponse = (response) => {
    const rawTask =
      response?.data?.data && !Array.isArray(response.data.data)
        ? response.data.data
        : response?.data && !Array.isArray(response.data)
        ? response.data
        : null;

    return rawTask ? buildTaskRow(rawTask, 0) : null;
  };

  const loadMachines = async () => {
    const response = await handleGetRequest(
      endPoints.getallMachine,
      false,
      false,
      dispatch
    );
    if (response?.data) {
      // Match Repair Orders machine display: "<serial/device> - <location>"
      const machinesWithLabel = response.data.map((m) => ({
        ...m,
        label: `${
          m.serialNumber && m.serialNumber.trim() !== ""
            ? m.serialNumber
            : m.deviceId && m.deviceId.trim() !== ""
            ? m.deviceId
            : t("Unknown")
        } - ${m.location || t("No location")}`,
      }));
      setMachines(machinesWithLabel);
      return true;
    }
    return false;
  };

  const loadExecutives = async () => {
    const response = await handleGetRequest(
      endPoints.getallUsers,
      false,
      false,
      dispatch
    );
    if (response?.data) {
      const allowedRoleUids = new Set(["operator", "technician"]);
      const filteredUsers = response.data
        .filter((user) => {
          const roleUid = user?.role?.uid || "";
          return (
            !user?.isDeleted &&
            user?.isActive !== false &&
            allowedRoleUids.has(roleUid)
          );
        })
        .map((user) => ({
          ...user,
          fullName:
            `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
            user?.email ||
            t("Unnamed user"),
        }))
        .sort((a, b) => a.fullName.localeCompare(b.fullName));

      const nextMaintenanceTeamIds = new Set(
        filteredUsers
          .map((user) => String(user.teamId?._id || user.teamId || ""))
          .filter(Boolean)
      );
      setMaintenanceTeamIds(nextMaintenanceTeamIds);
      setExecutives(filteredUsers);
      return nextMaintenanceTeamIds;
    }
    return false;
  };

  const loadTeams = async (allowedTeamIds = maintenanceTeamIds) => {
    const response = await handleGetRequest(
      endPoints.getallteams,
      false,
      false,
      dispatch
    );
    if (response?.data) {
      const activeTeams = response.data
        .filter((team) => {
          const teamId = String(team._id || "");
          return (
            !team.isDeleted &&
            team.isActive !== false &&
            allowedTeamIds.has(teamId)
          );
        })
        .map((team) => ({
          ...team,
          label: team.name || team.title || t("Unnamed team"),
          value: team._id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setTeams(activeTeams);
      return true;
    }
    return false;
  };

  const loadSpareParts = async () => {
    const response = await handleGetRequest(
      endPoints.getallPartsInventory,
      false,
      false,
      dispatch
    );
    if (response?.data) {
      // Filter only active parts (include all, even with 0 stock for reference)
      const activeParts = response.data
        .filter((part) => part.isActive && !part.isDeleted)
        .map((part) => ({
          _id: part._id,
          label: `${part.sku} - ${part.description}${
            part.quantityInStock !== undefined
              ? ` (${t("Stock")}: ${part.quantityInStock})`
              : ""
          }`,
          sku: part.sku,
          description: part.description,
          quantityInStock: part.quantityInStock || 0,
        }));
      setSpareParts(activeParts);
      return true;
    }
    return false;
  };

  const loadTasksByParams = async (params) => {
    const response = await handleGetRequest(
      endPoints.getMaintenanceTasks(params.toString()),
      false,
      false,
      dispatch
    );
    return response?.data ? normalizeTaskListResponse(response) : null;
  };

  const loadTasks = async () => {
    const pendingParams = new URLSearchParams();
    pendingParams.append("statusGroup", "pending");
    pendingParams.append("from", calendarRange.start.toISOString());
    pendingParams.append("to", calendarRange.end.toISOString());
    appendTechnicianScope(pendingParams);

    const closedParams = new URLSearchParams();
    closedParams.append("statusGroup", "closed");
    closedParams.append("completedFrom", new Date(new Date(closedRange.start).setHours(0, 0, 0, 0)).toISOString());
    closedParams.append("completedTo", new Date(new Date(closedRange.end).setHours(23, 59, 59, 999)).toISOString());
    closedParams.append("sortBy", "completedAt");
    closedParams.append("sortOrder", "desc");
    closedParams.append("page", "1");
    closedParams.append("limit", "500");
    closedParams.append("includeTotal", "false");
    appendTechnicianScope(closedParams);

    // Fetch every page so local filters and exports cover the entire period.
    const loadClosedPages = async () => {
      const tasks = [];
      let page = 1;
      while (true) {
        closedParams.set("page", String(page));
        const batch = await loadTasksByParams(closedParams);
        if (!batch) return null;
        tasks.push(...batch);
        if (batch.length < 500) return tasks;
        page += 1;
      }
    };

    const [pendingList, closedList] = await Promise.all([
      loadTasksByParams(pendingParams),
      loadClosedPages(),
    ]);

    if (!pendingList || !closedList) return false;

    const tasksById = new Map();
    [...pendingList, ...closedList].forEach((task) => {
      tasksById.set(task.taskId || task.id, task);
    });
    const list = Array.from(tasksById.values());

    setPmData(list);

    const categoriesSet = new Set();
    const routesSet = new Set();
    list.forEach((item) => {
      if (item.category) categoriesSet.add(item.category);
      if (item.route) routesSet.add(item.route);
    });

    setCategoryOptions([
      ...DEFAULT_CATEGORY_OPTIONS.map((option) => ({
        ...option,
        label: t(option.label),
      })),
      ...Array.from(categoriesSet).map((c) => ({ label: t(c), value: c })),
    ]);
    setRouteOptions([
      ...DEFAULT_ROUTE_OPTIONS,
      ...Array.from(routesSet).map((r) => ({ label: r, value: r })),
    ]);
    return true;
  };

  const loadReferenceData = async () => {
    if (referenceDataLoaded || loadingReferenceData) return true;

    setLoadingReferenceData(true);
    try {
      const maintenanceTeamIdSet = await loadExecutives();
      const results = await Promise.all([
        loadMachines(),
        maintenanceTeamIdSet === false ? false : loadTeams(maintenanceTeamIdSet),
        loadSpareParts(),
      ]);

      const success = results.every((result) => result !== false);
      if (success) {
        setReferenceDataLoaded(true);
      }
      return success;
    } finally {
      setLoadingReferenceData(false);
    }
  };

  const refreshModuleData = async ({ initial = false } = {}) => {
    if (initial) {
      setInitialLoading(true);
    } else {
      setRefreshingData(true);
    }

    setLoadError("");

    try {
      const tasksLoaded = await loadTasks();

      if (tasksLoaded === false) {
        setLoadError(
          t(
            "Some preventive maintenance data could not be loaded. You can retry without leaving the page."
          )
        );
      }

      if (initial && typeof window !== "undefined") {
        window.setTimeout(() => {
          loadReferenceData();
        }, 300);
      } else {
        loadReferenceData();
      }
    } finally {
      setInitialLoading(false);
      setRefreshingData(false);
    }
  };

  const refreshTasksData = async () => {
    setRefreshingData(true);
    setLoadError("");

    try {
      const result = await loadTasks();
      if (result === false) {
        setLoadError(
          t(
            "Some preventive maintenance data could not be loaded. You can retry without leaving the page."
          )
        );
      }
    } finally {
      setRefreshingData(false);
    }
  };

  const computeSummary = (data) => {
    const closedData = data.filter((p) => CLOSED_STATUSES.has(p.status));
    const today = formatLocalDateKey(new Date());
    const isOverdue = (p) =>
      p.status !== "Completed" &&
      (p.status === "Missed" ||
        (p.scheduledDateKey && p.scheduledDateKey < today));
    const completed = closedData.filter((p) => p.status === "Completed").length;
    const missed = closedData.filter(isOverdue).length;
    const inProgress = 0;
    const total = closedData.length;
    const adherenceRate =
      total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

    setSummary({
      total,
      completed,
      missed,
      inProgress,
      adherenceRate,
    });

    // Breakdown by category (task type) for closed tasks in the default recent range.
    const byCategory = {};
    closedData.forEach((p) => {
      const key = p.category || "Uncategorized";
      if (!byCategory[key]) {
        byCategory[key] = {
          category: key,
          total: 0,
          completed: 0,
          missed: 0,
          adherenceRate: 0,
        };
      }
      const bucket = byCategory[key];
      bucket.total += 1;
      if (p.status === "Completed") bucket.completed += 1;
      if (isOverdue(p)) bucket.missed += 1;
    });

    const categoryList = Object.values(byCategory).map((c) => ({
      ...c,
      adherenceRate:
        c.total > 0 ? ((c.completed / c.total) * 100).toFixed(1) : 0,
    }));

    setCategorySummary(categoryList);
  };

  useEffect(() => {
    refreshModuleData({ initial: true }).finally(() => {
      hasLoadedInitialData.current = true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasLoadedInitialData.current) return;
    refreshTasksData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarRange, closedRange]);

  useEffect(() => {
    const filtered = filterMaintenanceData(pmData, appliedFilters);
    setFilteredData(filtered);
    computeSummary(filtered);
  }, [pmData, appliedFilters]);

  useEffect(() => {
    const categoriesSet = new Set();
    const routesSet = new Set();
    const translate = (value) => translateUiText(value, preferredLanguage);
    pmData.forEach((item) => {
      if (item.category) categoriesSet.add(item.category);
      if (item.route) routesSet.add(item.route);
    });
    setCategoryOptions([
      ...DEFAULT_CATEGORY_OPTIONS.map((option) => ({
        ...option,
        label: translate(option.label),
      })),
      ...Array.from(categoriesSet).map((c) => ({
        label: translate(c),
        value: c,
      })),
    ]);
    setRouteOptions([
      ...DEFAULT_ROUTE_OPTIONS,
      ...Array.from(routesSet).map((r) => ({ label: r, value: r })),
    ]);
  }, [pmData, preferredLanguage]);

  useEffect(() => {
    const syncPreferredLanguage = () =>
      setPreferredLanguage(getPreferredLanguage());
    window.addEventListener(
      "preferred-language-changed",
      syncPreferredLanguage
    );
    window.addEventListener("storage", syncPreferredLanguage);

    return () => {
      window.removeEventListener(
        "preferred-language-changed",
        syncPreferredLanguage
      );
      window.removeEventListener("storage", syncPreferredLanguage);
    };
  }, []);

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const statusBodyTemplate = (rowData) => {
    const today = formatLocalDateKey(new Date());
    const isOverdue =
      rowData.status !== "Completed" &&
      (rowData.status === "Missed" ||
        (rowData.scheduledDateKey && rowData.scheduledDateKey < today));
    if (isOverdue) {
      return (
        <span style={{ color: "#ef4444", fontWeight: "bold" }}>
          {t("Overdue")}
        </span>
      );
    }
    const statusColors = {
      Completed: "#4CAF50",
      Scheduled: "#2196F3",
      "In Progress": "#eab308",
    };
    const color = statusColors[rowData.status] || "#9CA3AF";
    return (
      <span style={{ color, fontWeight: "bold" }}>
        {translateUiToken(rowData.status, preferredLanguage)}
      </span>
    );
  };

  const partsUsedBodyTemplate = (rowData) => {
    if (
      !rowData.rawPartsUsed ||
      !Array.isArray(rowData.rawPartsUsed) ||
      rowData.rawPartsUsed.length === 0
    ) {
      return <span className="text-500">-</span>;
    }
    return (
      <div>
        {rowData.rawPartsUsed.map((part, idx) => {
          // Handle both populated and unpopulated partId
          const partId =
            typeof part === "object" && part.partId
              ? typeof part.partId === "object"
                ? part.partId._id
                : part.partId
              : null;
          const partInfo = partId
            ? typeof part.partId === "object" && part.partId.sku
              ? part.partId
              : spareParts.find((p) => p._id === partId) || null
            : null;
          const quantity =
            typeof part === "object" && part.quantity ? part.quantity : "-";
          const sku = partInfo ? partInfo.sku || t("Unknown") : "-";
          return (
            <div key={idx} className="text-sm mb-1">
              {sku !== "-" ? `${sku} (Qty: ${quantity})` : "-"}
            </div>
          );
        })}
      </div>
    );
  };

  const completedDateBodyTemplate = (rowData) => {
    return rowData.completedDate
      ? new Date(rowData.completedDate).toLocaleDateString()
      : "-";
  };

  const taskIdBodyTemplate = (rowData) => {
    const rawId = rowData?.taskId ? String(rowData.taskId) : "";
    if (!rawId) return "-";
    return `#${rawId.slice(-4).toUpperCase()}`;
  };

  const todayKey = formatLocalDateKey(new Date());

  const machineFilterOptions = machines
    .filter((m) => !m?.isDeleted)
    .map((machine) => ({
      ...machine,
      label: normalizeMachineLabel(machine, t),
      value: machine._id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const teamFilterOptions = [
    { label: t("All Teams"), value: null },
    ...teams,
  ];
  const scheduleTaskCategoryOptions = getTaskCategoryOptionsForAssignee();
  const selectedScheduleAssignee = getSelectedAssignee();
  const scheduleAssigneeRoleUid = selectedScheduleAssignee?.role?.uid || "";
  const completionChecklistOptions = getCompletionChecklistOptions(
    completionForm.taskCategories || []
  );

  const dueDateBodyTemplate = (rowData) => {
    if (!rowData?.scheduledDateKey) return "-";
    const formatted = new Date(
      `${rowData.scheduledDateKey}T00:00:00`
    ).toLocaleDateString();
    const isOverdue =
      rowData.scheduledDateKey < todayKey && rowData.status !== "Completed";
    return (
      <span
        style={{
          color: isOverdue ? "#ef4444" : "inherit",
          fontWeight: isOverdue ? 600 : "normal",
        }}
      >
        {formatted}
      </span>
    );
  };

  const events = filteredData.map((task) => {
    const hasCustomer = task.customer && task.customer !== "-";
    const title = hasCustomer
      ? task.customer
      : task.route || t("Multiple machines");
    const color = resolveCalendarEventColor(task, todayKey);
    return {
      id: task.id,
      title,
      start: task.scheduledDateKey || task.scheduledDate,
      backgroundColor: color,
      borderColor: color,
      textColor: "#ffffff",
      extendedProps: { task },
    };
  });

  const handleCalendarEventClick = (info) => {
    const task = info?.event?.extendedProps?.task;
    if (task) {
      setCalendarDetailTask(task);
      setCalendarDetailVisible(true);
    }
  };

  const handleCalendarDatesSet = (dateInfo) => {
    const visibleStart = new Date(dateInfo.start);
    visibleStart.setHours(0, 0, 0, 0);

    const visibleEnd = new Date(dateInfo.end);
    visibleEnd.setMilliseconds(visibleEnd.getMilliseconds() - 1);
    visibleEnd.setHours(23, 59, 59, 999);

    setCalendarRange((current) => {
      if (
        formatLocalDateKey(current.start) === formatLocalDateKey(visibleStart) &&
        formatLocalDateKey(current.end) === formatLocalDateKey(visibleEnd)
      ) {
        return current;
      }

      return { start: visibleStart, end: visibleEnd };
    });
  };

  const openFullTaskDialog = () => {
    if (!calendarDetailTask) return;
    const task = calendarDetailTask;
    setCalendarDetailVisible(false);
    openCompletionDialog(task, true);
  };

  const openScheduleDialog = () => {
    loadReferenceData();
    setScheduleForm({
      taskCategories: [],
      preventiveType: isTechnician ? "technical" : DEFAULT_PREVENTIVE_TYPE,
      assignedTo: isTechnician ? currentAdmin?._id || "" : "",
      route: "",
      machineIds: [],
      scheduledDate: null,
      recurrenceRule: "none",
      comments: "",
      partsUsed: [],
      supervisorSignature: "",
    });
    setScheduleDialogVisible(true);
  };

  const addPartToSchedule = () => {
    setScheduleForm({
      ...scheduleForm,
      partsUsed: [...scheduleForm.partsUsed, { partId: "", quantity: 1 }],
    });
  };

  const removePartFromSchedule = (index) => {
    const updated = scheduleForm.partsUsed.filter((_, i) => i !== index);
    setScheduleForm({ ...scheduleForm, partsUsed: updated });
  };

  const updatePartInSchedule = (index, field, value) => {
    const updated = [...scheduleForm.partsUsed];
    updated[index] = { ...updated[index], [field]: value };
    setScheduleForm({ ...scheduleForm, partsUsed: updated });
  };

  const saveTask = async () => {
    if (savingSchedule) return;

    const payload = {
      taskType:
        scheduleForm.preventiveType === "technical"
          ? scheduleForm.taskCategories[0] || "Other"
          : "Other",
      taskCategories: scheduleForm.taskCategories,
      preventiveType: scheduleForm.preventiveType,
      assignedTo: scheduleForm.assignedTo,
      route: scheduleForm.route,
      machineIds: scheduleForm.machineIds,
      scheduledDate: scheduleForm.scheduledDate
        ? formatLocalDateKey(scheduleForm.scheduledDate)
        : null,
      recurrenceRule: scheduleForm.recurrenceRule,
      comments: scheduleForm.comments,
      partsUsed: scheduleForm.partsUsed.filter(
        (p) => p.partId && p.quantity > 0
      ),
      createdBy: currentAdmin?._id || null,
    };

    setSavingSchedule(true);
    try {
      const response = await dispatch(
        handlePostRequest(payload, endPoints.addMaintenanceTask, true)
      );
      if (response?.status === 201) {
        setScheduleDialogVisible(false);
        await refreshModuleData();
      }
    } finally {
      setSavingSchedule(false);
    }
  };

  const fetchTaskDetails = async (taskId) => {
    if (!taskId) return null;
    const response = await handleGetRequest(
      endPoints.getMaintenanceTask(taskId),
      false,
      false,
      dispatch
    );
    return response?.data ? normalizeTaskDetailResponse(response) : null;
  };

  const openCompletionDialog = async (task, viewOnly = false) => {
    loadReferenceData();
    let taskForDialog = task;
    if (viewOnly && task?.taskId) {
      const detailedTask = await fetchTaskDetails(task.taskId);
      if (detailedTask) {
        taskForDialog = detailedTask;
      }
    }

    // Handle parts - partId can be populated (object) or just an ID (string)
    const existingParts =
      taskForDialog.rawPartsUsed && Array.isArray(taskForDialog.rawPartsUsed)
        ? taskForDialog.rawPartsUsed.map((p) => {
            // Extract partId - could be an object (populated) or string (ID)
            let partId = null;
            if (typeof p === "object" && p.partId) {
              // If partId is populated, it's an object with _id, otherwise it's a string ID
              partId =
                typeof p.partId === "object"
                  ? p.partId._id || p.partId
                  : p.partId;
            } else if (typeof p === "string") {
              partId = p;
            }

            return {
              partId: partId,
              quantity: typeof p === "object" && p.quantity ? p.quantity : 1,
            };
          })
        : [];
    const existingPhotos = Array.isArray(taskForDialog.completionPhotos)
      ? taskForDialog.completionPhotos.filter(Boolean)
      : taskForDialog.completionPhoto
      ? [taskForDialog.completionPhoto]
      : [];
    setCompletionForm({
      taskId: taskForDialog.taskId,
      status: taskForDialog.status || "Scheduled",
      completionNotes: taskForDialog.completionNotes || "",
      taskCategories: taskForDialog.taskCategories || [],
      completionChecklistItems: taskForDialog.completionChecklistItems || [],
      completionPhotos: existingPhotos,
      partsUsed: existingParts.length > 0 ? existingParts : [],
      supervisorSignature: taskForDialog.supervisorSignature || "",
    });
    setIsViewMode(viewOnly);
    setCompletionDialogVisible(true);
  };

  const addPartToCompletion = () => {
    setCompletionForm({
      ...completionForm,
      partsUsed: [...completionForm.partsUsed, { partId: "", quantity: 1 }],
    });
  };

  const removePartFromCompletion = (index) => {
    const updated = completionForm.partsUsed.filter((_, i) => i !== index);
    setCompletionForm({ ...completionForm, partsUsed: updated });
  };

  const updatePartInCompletion = (index, field, value) => {
    const updated = [...completionForm.partsUsed];
    updated[index] = { ...updated[index], [field]: value };
    setCompletionForm({ ...completionForm, partsUsed: updated });
  };

  const saveCompletion = async () => {
    if (!completionForm.taskId || savingCompletion) return;

    const photos = (completionForm.completionPhotos || []).filter(Boolean);
    const payload = {
      status: completionForm.status,
      completionNotes: completionForm.completionNotes,
      completionChecklistItems: completionForm.completionChecklistItems || [],
      completionPhoto: photos[0] || null,
      completionPhotos: photos,
      partsUsed: completionForm.partsUsed.filter(
        (p) => p.partId && p.quantity > 0
      ),
      supervisorSignature: completionForm.supervisorSignature,
    };

    setSavingCompletion(true);
    try {
      const response = await dispatch(
        handlePostRequest(
          payload,
          endPoints.completeMaintenanceTask(completionForm.taskId),
          true
        )
      );
      if (response?.status === 200) {
        setCompletionDialogVisible(false);
        await refreshModuleData();
      }
    } finally {
      setSavingCompletion(false);
    }
  };

  const handleDeleteTask = (taskId) => {
    confirmDialog({
      message:
        "Are you sure you want to delete this task? This action cannot be undone.",
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        const response = await dispatch(
          handleDeleteRequest(endPoints.deleteMaintenanceTask(taskId), true)
        );
        if (response?.status === 200) {
          await refreshModuleData();
        }
      },
    });
  };

  const getExportPartsUsedText = (task) => {
    if (
      !task.rawPartsUsed ||
      !Array.isArray(task.rawPartsUsed) ||
      task.rawPartsUsed.length === 0
    ) {
      return "-";
    }

    return task.rawPartsUsed
      .map((part) => {
        const partId =
          typeof part === "object" && part.partId
            ? typeof part.partId === "object"
              ? part.partId._id
              : part.partId
            : null;
        const partInfo = partId
          ? typeof part.partId === "object" && part.partId.sku
            ? part.partId
            : spareParts.find((p) => p._id === partId) || null
          : null;
        const quantity =
          typeof part === "object" && part.quantity ? part.quantity : "-";
        const sku = partInfo ? partInfo.sku || "Unknown" : "-";
        return `${sku} (Qty: ${quantity})`;
      })
      .join("; ");
  };

  const buildExportData = (tasks) =>
    tasks.map((task) => ({
      "Task ID": task.taskId || "-",
      "Preventive Type": getPreventiveTypeLabel(task.preventiveType),
      Category: task.category || "-",
      Customer: task.customer || "-",
      Route: task.route || "-",
      Executive: task.executive || "-",
      "Machine ID": task.machineId || "-",
      "Machine Type": task.machineType || "-",
      "Scheduled Date": task.scheduledDate || "-",
      "Completed Date": task.completedDate
        ? new Date(task.completedDate).toLocaleDateString()
        : "-",
      Status: task.status || "-",
      "Completed Checklist":
        Array.isArray(task.completionChecklistItems) &&
        task.completionChecklistItems.length > 0
          ? task.completionChecklistItems.map((item) => t(item)).join("; ")
          : "-",
      "Parts Used": getExportPartsUsedText(task),
      "On Time":
        getTaskTimeliness(task)
          ? getTaskTimeliness(task) === "onTime"
            ? t("On time")
            : t("Late")
          : "-",
    }));

  const buildExportFilename = (baseName, filters) => {
    let filename = baseName;
    if (filters.startDate || filters.endDate) {
      const startStr = filters.startDate
        ? formatLocalDateKey(filters.startDate)
        : "all";
      const endStr = filters.endDate
        ? formatLocalDateKey(filters.endDate)
        : "all";
      filename += `_${startStr}_to_${endStr}`;
    }
    if (filters.category) {
      filename += `_${filters.category}`;
    }
    if (filters.preventiveType) {
      filename += `_${filters.preventiveType}`;
    }
    if (filters.route) {
      filename += `_${filters.route}`;
    }

    return filename;
  };

  const exportTaskSubset = (tasks, filters, baseName) => {
    try {
      if (tasks.length === 0) {
        setLoadError(t("There is no data to export for this section."));
        return;
      }

      exportToExcel(
        buildExportData(tasks),
        buildExportFilename(baseName, filters),
        null
      );
    } catch (error) {
      console.error("Error exporting maintenance tasks:", error);
      setLoadError(t("Failed to export data. Please try again."));
    }
  };

  const exportPendingTasks = () => {
    exportTaskSubset(
      filteredData.filter((task) => PENDING_STATUSES.has(task.status)),
      appliedFilters,
      "Preventive_Maintenance_Pending"
    );
  };

  const exportClosedTasks = () => {
    exportTaskSubset(
      closedTableTasks,
      { ...closedFilters, startDate: closedRange.start, endDate: closedRange.end },
      "Preventive_Maintenance_Closed"
    );
  };

  const pendingTableTasks = filteredData
    .filter((task) => PENDING_STATUSES.has(task.status))
    .sort((a, b) => {
      const left = a.scheduledDateKey || "";
      const right = b.scheduledDateKey || "";
      return left.localeCompare(right);
    });

  const closedTasks = pmData.filter((task) => CLOSED_STATUSES.has(task.status));
  const closedTableTasks = filterClosedTasks(closedTasks, closedFilters)
    .sort((a, b) => {
      const left = a.completedDate ? new Date(a.completedDate).getTime() : 0;
      const right = b.completedDate ? new Date(b.completedDate).getTime() : 0;
      return right - left;
    });

  const closedFilterOptions = (values, label = (value) => value) =>
    [...new Set(values.filter(Boolean))]
      .map((value) => ({ value, label: label(value) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  const closedFilterFields = [
    { key: "customer", label: "Customer", options: closedFilterOptions(closedTasks.flatMap((task) => task.customers)) },
    { key: "category", label: "Category", options: closedFilterOptions(closedTasks.flatMap((task) => task.taskCategories), getTaskCategoryLabel) },
    { key: "route", label: "Route", options: closedFilterOptions(closedTasks.map((task) => task.route)) },
    { key: "executive", label: "Operator", options: closedFilterOptions(closedTasks.map((task) => task.assignedToId), (id) => closedTasks.find((task) => task.assignedToId === id)?.executive || id) },
    { key: "machine", label: "Machine", options: closedFilterOptions(closedTasks.flatMap((task) => task.machineIds), (id) => {
      const machine = machines.find((item) => item._id === id);
      return machine ? `${machine.serialNumber || machine.deviceId || id} - ${machine.location || ""}` : id;
    }) },
    { key: "status", label: "Status", options: closedFilterOptions(closedTasks.map((task) => task.status), t) },
    { key: "timeliness", label: "On Time", options: [
      { label: t("Late"), value: "late" },
      { label: t("On time"), value: "onTime" },
    ] },
  ];

  useEffect(() => {
    setClosedFirst(0);
  }, [closedFilters, appliedFilters, pmData]);

  const pendingTotalCount = pendingTableTasks.length;
  const closedTotalCount = closedTableTasks.length;

  const renderMobileTaskCard = (task, isClosed = false) => (
    <article className="pm-task-card" key={task.id}>
      <div className="pm-task-card__header">
        <div>
          <small className="text-color-secondary">
            {taskIdBodyTemplate(task)}
          </small>
          <div className="pm-task-card__title">
            {t(task.category || "Other")}
          </div>
        </div>
        {statusBodyTemplate(task)}
      </div>

      <dl className="pm-task-card__summary">
        <div>
          <dt>{t(isClosed ? "Scheduled Date" : "Due Date")}</dt>
          <dd>{dueDateBodyTemplate(task)}</dd>
        </div>
        <div>
          <dt>{t("Machine ID")}</dt>
          <dd>{task.machineId || "-"}</dd>
        </div>
        <div>
          <dt>{t("Customer")}</dt>
          <dd>{task.customer || "-"}</dd>
        </div>
        <div>
          <dt>{t("Assigned Executive/Technician")}</dt>
          <dd>{task.executive || "-"}</dd>
        </div>
      </dl>

      <details className="pm-task-card__details">
        <summary>{t("View Full Details")}</summary>
        <dl>
          <div>
            <dt>{t("Preventive Type")}</dt>
            <dd>{getPreventiveTypeLabel(task.preventiveType)}</dd>
          </div>
          <div>
            <dt>{t("Route")}</dt>
            <dd>{task.route || "-"}</dd>
          </div>
          <div>
            <dt>{t("Machine Type")}</dt>
            <dd>{task.machineType || "-"}</dd>
          </div>
          {isClosed && (
            <>
              <div>
                <dt>{t("Completed Date")}</dt>
                <dd>{completedDateBodyTemplate(task)}</dd>
              </div>
              <div>
                <dt>{t("Parts Used")}</dt>
                <dd>{partsUsedBodyTemplate(task)}</dd>
              </div>
            </>
          )}
        </dl>
      </details>

      <div className="pm-task-card__actions">
        <Button
          label={isClosed ? t("View") : t("Update Status")}
          icon={isClosed ? "pi pi-eye" : "pi pi-check-circle"}
          className="p-button-outlined"
          onClick={() => openCompletionDialog(task, isClosed)}
        />
        {!isClosed &&
          !isTechnician &&
          !isOperator &&
          (task.status === "Scheduled" || task.status === "In Progress") && (
            <Button
              label={t("Delete")}
              icon="pi pi-trash"
              className="p-button-danger p-button-outlined"
              onClick={() => handleDeleteTask(task.taskId)}
            />
          )}
      </div>
    </article>
  );

  const renderScheduleDialogFooter = () => {
    const isDisabled =
      !scheduleForm.assignedTo ||
      !scheduleForm.preventiveType ||
      !Array.isArray(scheduleForm.taskCategories) ||
      scheduleForm.taskCategories.length === 0 ||
      !scheduleForm.scheduledDate ||
      !scheduleForm.recurrenceRule ||
      !scheduleForm.machineIds ||
      scheduleForm.machineIds.length === 0 ||
      savingSchedule;

    return (
      <div className="pm-dialog-actions flex justify-content-end gap-2">
        <Button
          label={t("Cancel")}
          icon="pi pi-times"
          className="p-button-text"
          onClick={() => setScheduleDialogVisible(false)}
        />
        <Button
          label={t("Save")}
          icon="pi pi-check"
          className="p-button-primary"
          onClick={saveTask}
          disabled={isDisabled}
          loading={savingSchedule}
        />
      </div>
    );
  };

  const renderCompletionDialogFooter = () => {
    if (isViewMode) {
      return (
        <div className="pm-dialog-actions flex justify-content-end">
          <Button
            label={t("Close")}
            icon="pi pi-times"
            className="p-button-text"
            onClick={() => setCompletionDialogVisible(false)}
          />
        </div>
      );
    }
    return (
      <div className="pm-dialog-actions flex justify-content-end gap-2">
        <Button
          label={t("Cancel")}
          icon="pi pi-times"
          className="p-button-text"
          onClick={() => setCompletionDialogVisible(false)}
        />
        <Button
          label={t("Save")}
          icon="pi pi-check"
          className="p-button-primary"
          onClick={saveCompletion}
          disabled={!completionForm.status || savingCompletion}
          loading={savingCompletion}
        />
      </div>
    );
  };

  return (
    <div className="pm-report">
      <ConfirmDialog />
      {initialLoading ? (
        <div className="card">
          <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
            <ProgressSpinner style={{ width: "48px", height: "48px" }} />
            <span className="text-color-secondary">
              {t("Loading preventive maintenance data...")}
            </span>
          </div>
        </div>
      ) : (
        <>
          <Dialog
            header={t("Schedule Maintenance Task")}
            visible={scheduleDialogVisible}
            style={{ width: "50rem", maxWidth: "calc(100vw - 2rem)" }}
            className="pm-responsive-dialog"
            modal
            onHide={() => setScheduleDialogVisible(false)}
            footer={renderScheduleDialogFooter()}
          >
            <div className="grid p-fluid">
              <div className="col-12 md:col-6">
                <label className="block mb-2">
                  {t("Assigned Executive/Technician")}{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <Dropdown
                  value={scheduleForm.assignedTo}
                  options={executives}
                  optionLabel="fullName"
                  optionValue="_id"
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      assignedTo: e.value,
                      preventiveType: getPreventiveTypeForAssignee(e.value),
                      taskCategories: [],
                    })
                  }
                  placeholder={t("Select operator or technician")}
                  className="w-full"
                  filter
                  filterBy="fullName,email"
                  disabled={isTechnician}
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block mb-2">
                  {t("Task Type")} <span style={{ color: "red" }}>*</span>
                </label>
                <MultiSelect
                  value={scheduleForm.taskCategories}
                  options={scheduleTaskCategoryOptions}
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      taskCategories: e.value || [],
                    })
                  }
                  placeholder={
                    scheduleForm.assignedTo
                      ? scheduleAssigneeRoleUid === "operator"
                        ? t("Select operator task categories")
                        : t("Select technical task categories")
                      : t("Select assigned person first")
                  }
                  className="w-full"
                  display="chip"
                  filter
                  disabled={!scheduleForm.assignedTo}
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block mb-2">{t("Route")}</label>
                <Dropdown
                  value={scheduleForm.route}
                  options={ROUTE_OPTIONS.filter(
                    (option) => option.value !== null
                  )}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) =>
                    setScheduleForm({ ...scheduleForm, route: e.value })
                  }
                  placeholder={t("Select Route")}
                  className="w-full"
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block mb-2">
                  {t("Machines")} <span style={{ color: "red" }}>*</span>
                </label>
                <MultiSelect
                  value={scheduleForm.machineIds}
                  options={machineFilterOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      machineIds: e.value || [],
                    })
                  }
                  placeholder={t("Select machines")}
                  className="w-full"
                  display="chip"
                  filter
                  filterBy="label"
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block mb-2">
                  {t("Scheduled Date")} <span style={{ color: "red" }}>*</span>
                </label>
                <Calendar
                  value={scheduleForm.scheduledDate}
                  onChange={(e) =>
                    setScheduleForm({ ...scheduleForm, scheduledDate: e.value })
                  }
                  dateFormat="yy-mm-dd"
                  placeholder={t("Select date")}
                  showIcon
                  className="w-full"
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block mb-2">
                  {t("Recurrence Rule")} <span style={{ color: "red" }}>*</span>
                </label>
                <Dropdown
                  value={scheduleForm.recurrenceRule}
                  options={recurrenceOptions}
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      recurrenceRule: e.value,
                    })
                  }
                  placeholder={t("Select recurrence")}
                  className="w-full"
                />
              </div>
              <div className="col-12">
                <label className="block mb-2">{t("Comments")}</label>
                <InputText
                  value={scheduleForm.comments}
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      comments: e.target.value,
                    })
                  }
                  placeholder={t("Additional details")}
                  className="w-full"
                />
              </div>
              <div className="col-12">
                <div className="flex justify-content-between align-items-center mb-2">
                  <label className="block mb-0">
                    {t("Parts Used (planned)")}
                  </label>
                  <Button
                    label={t("Add Part")}
                    icon="pi pi-plus"
                    className="p-button-text p-button-sm"
                    onClick={addPartToSchedule}
                  />
                </div>
                {scheduleForm.partsUsed.map((part, index) => (
                  <div key={index} className="grid mb-2">
                    <div className="col-12 md:col-6">
                      <Dropdown
                        value={part.partId}
                        options={spareParts}
                        optionLabel="label"
                        optionValue="_id"
                        onChange={(e) =>
                          updatePartInSchedule(index, "partId", e.value)
                        }
                        placeholder={t("Select part")}
                        className="w-full"
                        filter
                        filterBy="sku,description"
                        showClear
                      />
                    </div>
                    <div className="col-12 md:col-4">
                      <InputNumber
                        value={part.quantity}
                        onValueChange={(e) =>
                          updatePartInSchedule(index, "quantity", e.value || 1)
                        }
                        min={1}
                        max={
                          part.partId
                            ? spareParts.find((p) => p._id === part.partId)
                                ?.quantityInStock || 999
                            : 999
                        }
                        placeholder={t("Qty")}
                        className="w-full"
                      />
                    </div>
                    <div className="col-12 md:col-2">
                      <Button
                        icon="pi pi-times"
                        className="p-button-danger p-button-text p-button-sm w-full"
                        onClick={() => removePartFromSchedule(index)}
                        title={t("Remove part")}
                      />
                    </div>
                  </div>
                ))}
                {scheduleForm.partsUsed.length === 0 && (
                  <small className="text-500">
                    {t('No parts added. Click "Add Part" to add parts.')}
                  </small>
                )}
              </div>
              <div className="col-12">
                <label className="block mb-2">
                  {t("Supervisor Signature (text)")}
                </label>
                <InputText
                  value={scheduleForm.supervisorSignature}
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      supervisorSignature: e.target.value,
                    })
                  }
                  placeholder={t("Type supervisor name for approval")}
                  className="w-full"
                />
                <small className="text-500">
                  {t(
                    "This will be stored with timestamp when the task is marked completed."
                  )}
                </small>
              </div>
            </div>
          </Dialog>

          <Dialog
            header={
              isViewMode ? t("View Task Details") : t("Update Task Status")
            }
            visible={completionDialogVisible}
            style={{ width: "50rem", maxWidth: "calc(100vw - 2rem)" }}
            className="pm-responsive-dialog"
            modal
            onHide={() => setCompletionDialogVisible(false)}
            footer={renderCompletionDialogFooter()}
          >
            <div className="grid p-fluid">
              <div className="col-12 md:col-6">
                <label className="block mb-2">
                  {t("Status")} <span style={{ color: "red" }}>*</span>
                </label>
                <Dropdown
                  value={completionForm.status}
                  options={[
                    { label: t("In Progress"), value: "In Progress" },
                    { label: t("Completed"), value: "Completed" },
                    { label: t("Missed"), value: "Missed" },
                  ]}
                  onChange={(e) =>
                    setCompletionForm({
                      ...completionForm,
                      status: e.value,
                    })
                  }
                  placeholder={t("Select status")}
                  className="w-full"
                  disabled={isViewMode}
                />
                <small className="text-500">
                  {t(
                    'Setting to "In Progress" will update inventory quantities.'
                  )}
                </small>
              </div>
              <div className="col-12">
                <label className="block mb-2">{t("Completion Notes")}</label>
                <InputText
                  value={completionForm.completionNotes}
                  onChange={(e) =>
                    setCompletionForm({
                      ...completionForm,
                      completionNotes: e.target.value,
                    })
                  }
                  placeholder={t("Add notes about the task completion")}
                  className="w-full"
                  disabled={isViewMode}
                />
              </div>
              {completionChecklistOptions.length > 0 && (
                <div className="col-12">
                  <label className="block mb-2">
                    {t("Completed Checklist")}
                  </label>
                  <MultiSelect
                    value={completionForm.completionChecklistItems}
                    options={completionChecklistOptions}
                    onChange={(e) =>
                      setCompletionForm({
                        ...completionForm,
                        completionChecklistItems: e.value || [],
                      })
                    }
                    placeholder={t("Select completed tasks")}
                    className="w-full"
                    display="chip"
                    filter
                    disabled={isViewMode}
                  />
                </div>
              )}
              <div className="col-12">
                <PhotoUploader
                  label={t("Completion Photos")}
                  value=""
                  onChange={(val) => {
                    if (isViewMode) return;
                    const current = completionForm.completionPhotos || [];
                    if (current.length >= MAX_COMPLETION_PHOTOS) return;
                    setCompletionForm({
                      ...completionForm,
                      completionPhotos: [...current, val],
                    });
                  }}
                  helperText={t("Maximum 10 photos allowed.")}
                  disabled={
                    isViewMode ||
                    (completionForm.completionPhotos || []).length >=
                      MAX_COMPLETION_PHOTOS
                  }
                  maxUploadBytes={500 * 1024}
                />
                {(completionForm.completionPhotos || []).length > 0 && (
                  <div className="mt-2">
                    <small className="text-color-secondary">
                      {completionForm.completionPhotos.length} /{" "}
                      {MAX_COMPLETION_PHOTOS}{" "}
                      {t("photos")}
                    </small>
                    <div className="grid mt-1">
                      {completionForm.completionPhotos.map((url, idx) => (
                        <div
                          key={idx}
                          className="col-6 md:col-4 flex flex-column align-items-center gap-1"
                        >
                          <img
                            src={url}
                            alt={`${idx + 1}`}
                            style={{
                              width: "100%",
                              maxHeight: "120px",
                              objectFit: "cover",
                              borderRadius: "6px",
                              cursor: "pointer",
                            }}
                            onClick={() => setPhotoPreviewUrl(url)}
                          />
                          {!isViewMode && (
                            <Button
                              icon="pi pi-trash"
                              className="p-button-danger p-button-text p-button-sm"
                              onClick={() =>
                                setCompletionForm({
                                  ...completionForm,
                                  completionPhotos:
                                    completionForm.completionPhotos.filter(
                                      (_, i) => i !== idx
                                    ),
                                })
                              }
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="col-12">
                <div className="flex justify-content-between align-items-center mb-2">
                  <label className="block mb-0">
                    {t("Parts Used (actual)")}
                  </label>
                  {!isViewMode && (
                    <Button
                      label={t("Add Part")}
                      icon="pi pi-plus"
                      className="p-button-text p-button-sm"
                      onClick={addPartToCompletion}
                    />
                  )}
                </div>
                {completionForm.partsUsed.map((part, index) => {
                  // Get part info for display
                  const partInfo = part.partId
                    ? spareParts.find((p) => p._id === part.partId) || null
                    : null;
                  const partDisplay = partInfo
                    ? `${partInfo.sku || "Unknown"} - ${
                        partInfo.description || ""
                      }`
                    : t("Part not found");

                  return (
                    <div key={index} className="grid mb-2">
                      {isViewMode ? (
                        <>
                          <div className="col-12 md:col-8">
                            <InputText
                              value={partDisplay}
                              className="w-full"
                              disabled
                            />
                          </div>
                          <div className="col-12 md:col-4">
                            <InputNumber
                              value={part.quantity}
                              className="w-full"
                              disabled
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="col-12 md:col-6">
                            <Dropdown
                              value={part.partId}
                              options={spareParts}
                              optionLabel="label"
                              optionValue="_id"
                              onChange={(e) =>
                                updatePartInCompletion(index, "partId", e.value)
                              }
                              placeholder={t("Select part")}
                              className="w-full"
                              filter
                              filterBy="sku,description"
                              showClear
                            />
                          </div>
                          <div className="col-12 md:col-4">
                            <InputNumber
                              value={part.quantity}
                              onValueChange={(e) =>
                                updatePartInCompletion(
                                  index,
                                  "quantity",
                                  e.value || 1
                                )
                              }
                              min={1}
                              max={
                                part.partId
                                  ? spareParts.find(
                                      (p) => p._id === part.partId
                                    )?.quantityInStock || 999
                                  : 999
                              }
                              placeholder={t("Qty")}
                              className="w-full"
                            />
                          </div>
                          <div className="col-12 md:col-2">
                            <Button
                              icon="pi pi-times"
                              className="p-button-danger p-button-text p-button-sm w-full"
                              onClick={() => removePartFromCompletion(index)}
                              title="Remove part"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
                {completionForm.partsUsed.length === 0 && (
                  <small className="text-500">
                    No parts added. Click "Add Part" to add parts.
                  </small>
                )}
              </div>
              <div className="col-12">
                <label className="block mb-2">
                  {t("Supervisor Signature")}
                </label>
                <InputText
                  value={completionForm.supervisorSignature}
                  onChange={(e) =>
                    setCompletionForm({
                      ...completionForm,
                      supervisorSignature: e.target.value,
                    })
                  }
                  placeholder={t("Type supervisor name to sign off")}
                  className="w-full"
                  disabled={isViewMode}
                />
                <small className="text-500">
                  {t(
                    "Signature and timestamp will be stored when you save this update."
                  )}
                </small>
              </div>
            </div>
          </Dialog>

          {loadError ? (
            <div
              className="card mb-4"
              style={{
                borderLeft: "4px solid #dc2626",
                background: "#fff7f7",
              }}
            >
              <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
                <div>
                  <div className="font-semibold text-900 mb-1">
                    {t("Data load issue")}
                  </div>
                  <div className="text-color-secondary">{loadError}</div>
                </div>
                <Button
                  label={t("Retry")}
                  icon="pi pi-refresh"
                  className="p-button-outlined p-button-danger"
                  onClick={() => refreshModuleData()}
                  loading={refreshingData}
                />
              </div>
            </div>
          ) : null}

          <div className="card mb-4 pm-filters-card">
            <div className="pm-section-toolbar flex justify-content-between align-items-center mb-3">
              <h4 className="m-0">{t("Filters")}</h4>
              <div className="pm-toolbar-actions flex gap-2">
                <Button
                  label={
                    mobileFiltersVisible ? t("Hide Filters") : t("Show Filters")
                  }
                  icon={
                    mobileFiltersVisible
                      ? "pi pi-chevron-up"
                      : "pi pi-sliders-h"
                  }
                  className="p-button-text p-button-sm pm-filter-toggle"
                  onClick={() =>
                    setMobileFiltersVisible((visible) => !visible)
                  }
                  aria-expanded={mobileFiltersVisible}
                />
                <Button
                  label={t("Refresh")}
                  icon="pi pi-refresh"
                  className="p-button-outlined p-button-sm"
                  onClick={() => refreshModuleData()}
                  loading={refreshingData}
                />
                {!isOperator && !isTechnician && (
                  <Button
                    label={t("Schedule Task")}
                    icon="pi pi-plus"
                    className="p-button-success p-button-sm"
                    onClick={openScheduleDialog}
                  />
                )}
                {isTechnician && (
                  <Button
                    label={t("Add Task")}
                    icon="pi pi-plus"
                    className="p-button-success p-button-sm"
                    onClick={openScheduleDialog}
                  />
                )}
              </div>
            </div>
            <div
              className={`pm-filters-content ${
                mobileFiltersVisible ? "is-open" : ""
              }`}
            >
              <div className="grid">
                <div className="col-12 md:col-3">
                <label className="block mb-2">{t("Start Date")}</label>
                <Calendar
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.value })
                  }
                  dateFormat="yy-mm-dd"
                  placeholder={t("Select Start Date")}
                  showIcon
                  className="w-full"
                />
                </div>
                <div className="col-12 md:col-3">
                <label className="block mb-2">{t("End Date")}</label>
                <Calendar
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.value })}
                  dateFormat="yy-mm-dd"
                  placeholder={t("Select End Date")}
                  showIcon
                  className="w-full"
                />
                </div>
                <div className="col-12 md:col-3">
                <label className="block mb-2">{t("Category")}</label>
                <Dropdown
                  value={filters.category}
                  options={categoryOptions}
                  onChange={(e) =>
                    setFilters({ ...filters, category: e.value })
                  }
                  placeholder={t("Select Category")}
                  className="w-full"
                />
                </div>
                <div className="col-12 md:col-3">
                <label className="block mb-2">{t("Team")}</label>
                <Dropdown
                  value={filters.teamId}
                  options={teamFilterOptions}
                  onChange={(e) =>
                    setFilters({ ...filters, teamId: e.value })
                  }
                  optionLabel="label"
                  optionValue="value"
                  placeholder={t("Select Team")}
                  className="w-full"
                  filter
                  filterBy="label"
                />
                </div>
                <div className="col-12 md:col-3">
                <label className="block mb-2">{t("Route")}</label>
                <Dropdown
                  value={filters.route}
                  options={routeOptions}
                  onChange={(e) => setFilters({ ...filters, route: e.value })}
                  placeholder={t("Select Route")}
                  className="w-full"
                />
                </div>
                <div className="col-12 md:col-3">
                <label className="block mb-2">{t("Machines")}</label>
                <MultiSelect
                  value={filters.machineIds}
                  options={machineFilterOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) =>
                    setFilters({ ...filters, machineIds: e.value || [] })
                  }
                  placeholder={t(
                    "Type a machine, location, serial, device ID, or UUID"
                  )}
                  className="w-full"
                  display="chip"
                  filter
                  filterBy="label"
                  maxSelectedLabels={2}
                />
                </div>
                {!isOperator && (
                  <div className="col-12 md:col-3">
                  <label className="block mb-2">{t("Executive")}</label>
                  <Dropdown
                    value={filters.executive}
                    options={executives}
                    optionLabel="fullName"
                    optionValue="_id"
                    onChange={(e) =>
                      setFilters({ ...filters, executive: e.value })
                    }
                    placeholder={t("Select operator or technician")}
                    className="w-full"
                    filter
                    filterBy="fullName,email"
                    showClear
                  />
                  </div>
                )}
              </div>
              <div className="pm-filter-actions flex gap-2 mt-3">
                <Button
                  label={t("Apply Filters")}
                  icon="pi pi-filter"
                  onClick={applyFilters}
                  className="p-button-primary"
                />
                <Button
                  label={t("Reset")}
                  icon="pi pi-refresh"
                  onClick={resetFilters}
                  className="p-button-secondary"
                />
              </div>
            </div>
          </div>

          <div className="grid mb-4">
            <div className="col-12 sm:col-6 md:col-2">
              <Card>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {summary.total}
                  </div>
                  <div className="text-color-secondary">{t("Total Tasks")}</div>
                </div>
              </Card>
            </div>
            <div className="col-12 sm:col-6 md:col-2">
              <Card>
                <div className="text-center">
                  <div
                    className="text-2xl font-bold"
                    style={{ color: "#4CAF50" }}
                  >
                    {summary.completed}
                  </div>
                  <div className="text-color-secondary">{t("Completed")}</div>
                </div>
              </Card>
            </div>
            <div className="col-12 sm:col-6 md:col-2">
              <Card>
                <div className="text-center">
                  <div
                    className="text-2xl font-bold"
                    style={{ color: "#eab308" }}
                  >
                    {summary.inProgress || 0}
                  </div>
                  <div className="text-color-secondary">{t("In Progress")}</div>
                </div>
              </Card>
            </div>
            <div className="col-12 sm:col-6 md:col-2">
              <Card>
                <div className="text-center">
                  <div
                    className="text-2xl font-bold"
                    style={{ color: "#F44336" }}
                  >
                    {summary.missed}
                  </div>
                  <div className="text-color-secondary">{t("Missed")}</div>
                </div>
              </Card>
            </div>
            <div className="col-12 sm:col-6 md:col-2">
              <Card>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {summary.adherenceRate}%
                  </div>
                  <div className="text-color-secondary">
                    {t("Adherence Rate")}
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {categorySummary.length > 0 && (
            <div className="card mb-4">
              <h4 className="mb-3">
                {t("Adherence by Category (current filters)")}
              </h4>
              <DataTable
                value={categorySummary}
                rows={10}
                paginator={categorySummary.length > 10}
                emptyMessage={t("No category data available.")}
                responsiveLayout="scroll"
              >
                <Column
                  field="category"
                  header={t("Category")}
                  body={(row) => t(row.category)}
                />
                <Column field="total" header={t("Total Tasks")} />
                <Column field="completed" header={t("Completed")} />
                <Column field="missed" header={t("Missed")} />
                <Column
                  field="adherenceRate"
                  header={t("Adherence %")}
                  body={(row) => `${row.adherenceRate}%`}
                />
              </DataTable>
            </div>
          )}

          <div className="card mb-4 pm-calendar-card">
            <div className="flex justify-content-between align-items-center flex-wrap mb-3">
              <h4 className="m-0">{t("Preventive Maintenance Calendar")}</h4>
              <div className="flex flex-wrap gap-3 align-items-center">
                {[
                  { label: "Completed", color: CALENDAR_STATUS_COLORS.completed },
                  { label: "Overdue", color: CALENDAR_STATUS_COLORS.overdue },
                  ...TASK_TYPE_OPTIONS.map((opt) => ({
                    label: opt.value,
                    color: getTaskTypeColor(opt.value),
                  })),
                ].map((chip) => (
                  <div
                    key={chip.label}
                    className="flex align-items-center gap-2"
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "14px",
                        height: "14px",
                        borderRadius: "3px",
                        backgroundColor: chip.color,
                      }}
                    />
                    <small className="text-color-secondary">
                      {t(chip.label)}
                    </small>
                  </div>
                ))}
              </div>
            </div>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin]}
              locale={preferredLanguage === "es" ? esLocale : "en"}
              initialView="dayGridMonth"
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              events={events}
              eventClick={handleCalendarEventClick}
              datesSet={handleCalendarDatesSet}
              dayMaxEvents={3}
              eventDisplay="block"
              height="600px"
            />
          </div>

          <Dialog
            header={t("Task Details")}
            visible={calendarDetailVisible}
            style={{ width: "420px", maxWidth: "95vw" }}
            className="pm-responsive-dialog pm-task-detail-dialog"
            onHide={() => setCalendarDetailVisible(false)}
            footer={
              <div className="flex justify-content-end gap-2">
                <Button
                  label={t("Close")}
                  icon="pi pi-times"
                  className="p-button-text"
                  onClick={() => setCalendarDetailVisible(false)}
                />
                <Button
                  label={t("View Full Details")}
                  icon="pi pi-external-link"
                  onClick={openFullTaskDialog}
                />
              </div>
            }
          >
            {calendarDetailTask && (
              <div className="flex flex-column gap-3">
                <div className="flex align-items-center gap-2 flex-wrap">
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 10px",
                      borderRadius: "999px",
                      backgroundColor: resolveCalendarEventColor(
                        calendarDetailTask,
                        todayKey
                      ),
                      color: "#ffffff",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    {t(calendarDetailTask.category || "Other")}
                  </span>
                  <span className="text-color-secondary" style={{ fontSize: "0.85rem" }}>
                    {calendarDetailTask.scheduledDateKey
                      ? new Date(
                          `${calendarDetailTask.scheduledDateKey}T00:00:00`
                        ).toLocaleDateString()
                      : "-"}
                  </span>
                </div>
                <div>
                  <div className="text-color-secondary" style={{ fontSize: "0.8rem" }}>
                    {t("Customer")}
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {calendarDetailTask.customer && calendarDetailTask.customer !== "-"
                      ? calendarDetailTask.customer
                      : calendarDetailTask.route || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-color-secondary" style={{ fontSize: "0.8rem" }}>
                    {t("Preventive Type")}
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {getPreventiveTypeLabel(calendarDetailTask.preventiveType)}
                  </div>
                </div>
                <div>
                  <div className="text-color-secondary" style={{ fontSize: "0.8rem" }}>
                    {t("Assigned Executive/Technician")}
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {calendarDetailTask.executive || "-"}
                  </div>
                </div>
                <div>
                  <div className="text-color-secondary" style={{ fontSize: "0.8rem" }}>
                    {t("Comments")}
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {(calendarDetailTask.status === "Completed"
                      ? calendarDetailTask.completionNotes
                      : calendarDetailTask.scheduledComments) || "-"}
                  </div>
                </div>
              </div>
            )}
          </Dialog>

          <Dialog
            visible={!!photoPreviewUrl}
            onHide={() => setPhotoPreviewUrl(null)}
            header={t("Photo")}
            style={{ width: "auto", maxWidth: "90vw" }}
            dismissableMask
          >
            {photoPreviewUrl && (
              <img
                src={photoPreviewUrl}
                alt=""
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
              />
            )}
          </Dialog>

          <div className="card mb-4">
            <div className="pm-section-toolbar flex justify-content-between align-items-center mb-3">
              <div>
                <h4 className="m-0">{t("Pending Maintenance Tasks")}</h4>
                <small className="text-color-secondary">
                  {pendingTotalCount}{" "}
                  {pendingTotalCount === 1
                    ? t("pending task")
                    : t("pending tasks")}
                </small>
              </div>
              <div className="pm-toolbar-actions flex gap-2">
                <Button
                  label={t("Export to Excel")}
                  icon="pi pi-file-excel"
                  className="p-button-success p-button-sm"
                  onClick={exportPendingTasks}
                />
              </div>
            </div>
            <div className="pm-mobile-task-list">
              {pendingTableTasks.length > 0 ? (
                pendingTableTasks.map((task) =>
                  renderMobileTaskCard(task, false)
                )
              ) : (
                <div className="pm-mobile-empty">
                  {t("No pending maintenance tasks found.")}
                </div>
              )}
            </div>
            <DataTable
              className="pm-desktop-table"
              value={pendingTableTasks}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 20]}
              emptyMessage={t("No pending maintenance tasks found.")}
              responsiveLayout="scroll"
            >
              <Column
                field="taskId"
                header={t("Task ID")}
                body={taskIdBodyTemplate}
              />
              <Column
                field="category"
                header={t("Category")}
                body={(row) => t(row.category)}
              />
              <Column
                field="preventiveType"
                header={t("Preventive Type")}
                body={(row) => getPreventiveTypeLabel(row.preventiveType)}
              />
              <Column field="customer" header={t("Customer")} />
              <Column field="route" header={t("Route")} />
              <Column field="executive" header={t("Executive")} />
              <Column field="machineId" header={t("Machine ID")} />
              <Column field="machineType" header={t("Machine Type")} />
              <Column
                field="scheduledDateKey"
                header={t("Due Date")}
                body={dueDateBodyTemplate}
                sortable
              />
              <Column
                field="status"
                header={t("Status")}
                body={statusBodyTemplate}
              />
              <Column
                header={t("Actions")}
                body={(row) => (
                  <div className="flex gap-2">
                    {row.status === "Completed" || row.status === "Missed" ? (
                      <Button
                        label={t("View")}
                        icon="pi pi-eye"
                        className="p-button-text p-button-sm"
                        onClick={() => openCompletionDialog(row, true)}
                      />
                    ) : (
                      <Button
                        label={t("Update Status")}
                        icon="pi pi-check-circle"
                        className="p-button-text p-button-sm"
                        onClick={() => openCompletionDialog(row, false)}
                      />
                    )}
                    {!isTechnician &&
                      !isOperator &&
                      (row.status === "Scheduled" ||
                        row.status === "In Progress") && (
                        <Button
                          label={t("Delete")}
                          icon="pi pi-trash"
                          className="p-button-danger p-button-text p-button-sm"
                          onClick={() => handleDeleteTask(row.taskId)}
                        />
                      )}
                  </div>
                )}
              />
            </DataTable>
          </div>

          <div className="card">
            <div className="pm-section-toolbar flex justify-content-between align-items-center mb-3">
              <div>
                <h4 className="m-0">{t("Closed Maintenance Tasks")}</h4>
                <small className="text-color-secondary">
                  {closedTotalCount}{" "}
                  {closedTotalCount === 1 ? t("closed task") : t("closed tasks")}
                </small>
              </div>
              <div className="pm-toolbar-actions flex gap-2">
                <Button
                  label={t("Export to Excel")}
                  icon="pi pi-file-excel"
                  className="p-button-success p-button-sm"
                  onClick={exportClosedTasks}
                />
              </div>
            </div>
            <div className="grid mb-3 pm-filters-card">
              {[{ key: "start", label: "Start Date" }, { key: "end", label: "End Date" }].map(({ key, label }) => (
                <div className="col-12 sm:col-6 lg:col-3" key={key}>
                  <label htmlFor={`closed-date-${key}`} className="block mb-2">{t(label)} ({t("Completed Date")})</label>
                  <Calendar
                    inputId={`closed-date-${key}`}
                    value={closedDateDraft[key]}
                    onChange={(event) => setClosedDateDraft((current) => ({ ...current, [key]: event.value }))}
                    minDate={key === "end" ? closedDateDraft.start : undefined}
                    maxDate={key === "start" ? closedDateDraft.end : undefined}
                    dateFormat="yy-mm-dd"
                    showIcon
                    readOnlyInput
                    disabled={initialLoading || refreshingData}
                    className="w-full"
                  />
                </div>
              ))}
              <div className="col-12 sm:col-6 lg:col-3 flex align-items-end">
                <Button label={t("Apply Filters")} icon="pi pi-filter" className="w-full"
                  disabled={initialLoading || refreshingData || !closedDateDraft.start || !closedDateDraft.end || closedDateDraft.start > closedDateDraft.end}
                  onClick={() => setClosedRange({ ...closedDateDraft })} />
              </div>
              {closedFilterFields.map(({ key, label, options }) => (
                <div className="col-12 sm:col-6 lg:col-2" key={key}>
                  <label htmlFor={`closed-filter-${key}`} className="block mb-2">{t(label)}</label>
                  <Dropdown
                    inputId={`closed-filter-${key}`}
                    value={closedFilters[key] || null}
                    options={options}
                    onChange={(event) => setClosedFilters((current) => ({ ...current, [key]: event.value }))}
                    placeholder={t("All")}
                    showClear
                    filter={key !== "timeliness"}
                    className="w-full"
                  />
                </div>
              ))}
              <div className="col-12 sm:col-6 lg:col-2 flex align-items-end">
                <Button label={t("Reset")} icon="pi pi-refresh" className="p-button-secondary w-full" disabled={initialLoading || refreshingData} onClick={() => {
                  const range = getClosedTaskDefaultRange();
                  setClosedFilters({});
                  setClosedDateDraft(range);
                  setClosedRange(range);
                }} />
              </div>
            </div>
            <div className="pm-mobile-task-list">
              {closedTableTasks.length > 0 ? (
                closedTableTasks.map((task) =>
                  renderMobileTaskCard(task, true)
                )
              ) : (
                <div className="pm-mobile-empty">
                  {t("No closed maintenance tasks found.")}
                </div>
              )}
            </div>
            <DataTable
              className="pm-desktop-table"
              value={closedTableTasks}
              first={closedFirst}
              onPage={(event) => {
                setClosedFirst(event.first);
                setClosedRows(event.rows);
              }}
              paginator
              rows={closedRows}
              rowsPerPageOptions={[5, 10, 20]}
              emptyMessage={t("No closed maintenance tasks found.")}
              responsiveLayout="scroll"
            >
              <Column
                field="taskId"
                header={t("Task ID")}
                body={taskIdBodyTemplate}
              />
              <Column
                field="category"
                header={t("Category")}
                body={(row) => t(row.category)}
              />
              <Column
                field="preventiveType"
                header={t("Preventive Type")}
                body={(row) => getPreventiveTypeLabel(row.preventiveType)}
              />
              <Column field="customer" header={t("Customer")} />
              <Column field="route" header={t("Route")} />
              <Column field="executive" header={t("Executive")} />
              <Column field="machineId" header={t("Machine ID")} />
              <Column field="machineType" header={t("Machine Type")} />
              <Column
                field="scheduledDate"
                header={t("Scheduled Date")}
                body={(row) =>
                  row.scheduledDateKey
                    ? new Date(
                        `${row.scheduledDateKey}T00:00:00`
                      ).toLocaleDateString()
                    : "-"
                }
              />
              <Column
                field="completedDate"
                header={t("Completed Date")}
                body={completedDateBodyTemplate}
              />
              <Column
                field="status"
                header={t("Status")}
                body={statusBodyTemplate}
              />
              <Column
                field="partsUsed"
                header={t("Parts Used")}
                body={partsUsedBodyTemplate}
              />
              <Column
                header={t("On Time")}
                body={(row) => {
                  const timeliness = getTaskTimeliness(row);
                  if (!timeliness) return "-";
                  const onTime = timeliness === "onTime";
                  return (
                    <span
                      style={{
                        color: onTime ? "#22c55e" : "#ef4444",
                        fontWeight: 600,
                      }}
                    >
                      {onTime ? t("On time") : t("Late")}
                    </span>
                  );
                }}
              />
              <Column
                header={t("Actions")}
                body={(row) => (
                  <div className="flex gap-2">
                    <Button
                      label={t("View")}
                      icon="pi pi-eye"
                      className="p-button-text p-button-sm"
                      onClick={() => openCompletionDialog(row, true)}
                    />
                  </div>
                )}
              />
            </DataTable>
          </div>
        </>
      )}
    </div>
  );
};

export default PreventiveMaintenanceReport;
